/**
 * What a customer still owes.
 *
 * Jonathon, 2026-09-06: "on the overview page there should be a remaining
 * balance on approved or (sent jobs) so we can keep up with who owes us money
 * and how much." Nothing in Roof Board worked this out before — the contract
 * was on one screen, the payment schedule on another, and whether the money had
 * arrived on a third.
 *
 * ## What counts as paid
 *
 * **The money being in the Mercury account.** His instruction, on the same day,
 * overruling the first version of this file: "what counts as paid should be
 * when that specific money hits our bank account in mercury."
 *
 * That first version read the `First Check` / `Final Check` boxes instead. It
 * was wrong in the way that matters: a box is somebody's opinion that the money
 * has arrived, and a board of who-owes-us-what has to be built on the money
 * itself. A box ticked early makes a debt disappear off the screen that is
 * supposed to be chasing it.
 *
 * One thing says the money is in: a credit on the Mercury account, confirmed
 * against this job — by a person, by an identifier Mercury put on the bank line
 * (ADR 0142), or by Stripe naming who was inside a card payout (ADR 0138). Cash
 * and a cleared check count too, because they are money in hand (ADR 0125).
 *
 * **A payment request Mercury calls `paid` does not**, from 2026-09-12. That is
 * Mercury's answer about an invoice — the payment was *taken* — and for a card
 * it is two or three days before the money exists anywhere but Stripe. Counting
 * it was the same mistake as reading the checklist box, one layer along: a
 * debt disappearing off the screen that is supposed to be chasing it
 * (ADR 0139). `slotsInTheBank` in moneyInTheBank.ts is where that is stated.
 *
 * Slots are what is recorded, which is what makes the sources safe to take
 * together: one payment can be seen twice — as a confirmed credit and as a
 * check somebody also recorded — but it is one slot either way and a slot is
 * paid once.
 *
 * Money the company takes through QuickBooks is included without being named
 * here, because a QuickBooks Payments deposit lands in the Mercury account like
 * any other and is confirmed there.
 *
 * ## What this does not see
 *
 * **Cash and cheques.** They go to Wells Fargo or the cash box and never touch
 * Mercury, so a job settled that way goes on reading as owed. That is the
 * consequence of the rule as asked for, and it is stated here rather than
 * quietly patched with a checklist fallback — the fallback is exactly what he
 * overruled.
 *
 * ## What is not in it
 *
 * The 3% card fee. It is not part of the contract (see `ladder.ts`), so it is
 * not part of the balance; a customer who pays by card settles their balance
 * and pays a fee on top.
 *
 * And the difference between what the customer paid and what reached the bank.
 * A card payment lands net of the processor's cut, and that shortfall is a cost
 * of doing the job (ADR 0095), not a debt the customer still owes. So a paid
 * slot clears what that slot was **owed**, not what the bank received.
 *
 * ## One number, everywhere
 *
 * Jonathon, 2026-09-08: "that remaining balance needs to correspond throughout
 * roofboard so we don't send an email to a customer that says they only x when
 * they actually owe y."
 *
 * So this file is the only place that answers "how much is left". The ongoing
 * board, the balance card on a job's Overview, the same card on its Estimate,
 * and the amount the balance letter asks for all come out of `jobBalance`.
 * Anything that works out an amount owed by subtracting for itself is a second
 * answer, and a second answer is the bug he is describing.
 *
 * The one thing that is deliberately NOT this number is what a single payment
 * request bills. A request settles one slot; the balance is the whole debt.
 * They differ exactly when an earlier payment never arrived, and where that
 * matters — the letter — it is said in words rather than papered over with an
 * amount the link cannot actually charge.
 *
 * Which is why what a request billed is an input here (`billedBySlot`) rather
 * than something read off the schedule. A balance request bills what is
 * outstanding less anything already on a live link, so slot 4 can be worth the
 * whole job or a fraction of it, and only the request itself knows which. This
 * file treated slot 4 as settling everything until 2026-09-08, and a customer
 * who paid a part-balance link while an unpaid deposit link was still out
 * there read as paid up.
 */

import { PAYMENT_SLOT_LABEL } from "./constants.js";
import { formatMoney, type Cents, cents } from "./money.js";
import { expectedPayments, type PaymentSlot } from "./paymentReceipt.js";

export interface BalanceInput {
  /** What the customer agreed to pay. Zero on a job with no price yet. */
  contract: Cents;
  /** The typed first payment, when a rep set one. Null takes the schedule's. */
  firstCents?: Cents | null;
  /**
   * What the customer signed, before any change order signed since.
   *
   * The deposit is half of that and stays half of that, so a change order
   * signed after the deposit arrived adds its whole amount to the Final rather
   * than half of it to each (ADR 0205). Without it the deposit that had
   * already been collected was valued at the new, bigger schedule row, and the
   * balance came out short by half the change order — Brian Garrett, job 1004,
   * $326.50 never asked for.
   *
   * `contractAsSigned` in quickbooks.ts, always; null on a job nobody has
   * signed, which is what this did for everybody before.
   */
  signedContractCents?: Cents | null;
  /**
   * The middle draws — slot 2 and slot 3 — where the rep typed any.
   *
   * Most jobs have none. On the ones that do, a paid draw is money in the
   * account and has to come off the balance; without this it did not, and a
   * customer who had paid three of four instalments still read as owing three
   * quarters of the job.
   */
  middleCents?: Readonly<Record<number, Cents | null>> | null;
  /**
   * The payment slots whose money is in the Mercury account.
   *
   * 1 is the deposit, 2 and 3 the draws, 4 the balance. Duplicates are
   * harmless — a slot is paid once however many sources say so.
   */
  paidSlots: readonly number[];
  /**
   * Money the customer has already sent that the bank has not seen.
   *
   * `alreadyBeingCollected` in moneyInTheBank.ts works it out, and the balance
   * letter has asked for the debt less this figure since ADR 0139. From
   * 2026-09-14 the screens say it too: Josh found the letter asking Cat
   * Edwards for $6,630 and telling her $6,630 was on its way while the job
   * screen and the production board both read $13,260 owed. Jonathon, asked
   * which is right: show it as on the way, everywhere.
   *
   * It does not change `outstanding`. A card payment sits at Stripe for two or
   * three days and the debt is not settled until the money is there — that is
   * the whole of ADR 0139 and it has not moved. What this adds is the second
   * half of the sentence the letter already says: this much is owed, this much
   * is coming, so this much is what we are asking for.
   */
  onItsWay?: Cents | null;
  /**
   * What a paid slot's Mercury request actually billed, where one did.
   *
   * Usually the same as the schedule row, and absent for money a person
   * matched off the bank statement rather than money Mercury invoiced — both
   * of which fall back to the schedule.
   *
   * It matters on slot 4, because a balance request does not bill the Final
   * row: `raiseBalanceRequest` bills whatever is genuinely outstanding, less
   * anything the customer is already being asked for on a live link. So the
   * one payment can settle a whole job that was never asked for a deposit, and
   * can equally settle only part of one — and the schedule row alone cannot
   * tell those two apart.
   */
  billedBySlot?: Readonly<Record<number, Cents | null>> | null;
  /**
   * Money paid against this job that settles no scheduled payment.
   *
   * A link somebody raised by hand (ADR 0141) — half a balance a customer
   * wanted to split, a change order — has no slot, so none of the arithmetic
   * above can see it. Jonathon, asked on 2026-09-13 what a paid one should do:
   * it comes off what they owe, and off the final bill. An unpaid one does
   * nothing at all until it is paid, which is why only paid links reach here.
   */
  offScheduleCents?: Cents | null;
  /**
   * Checks and cash recorded toward an in-house payment plan (ADR 0286).
   *
   * Counted at what was handed over, the way an off-schedule payment is: a
   * $100 monthly check settles $100, not the whole Final it belongs to. Until
   * 2026-10-14 a check that named no slot did not come off the balance at
   * all, which is why Durward's monthly checks could not have been recorded.
   */
  planPaidCents?: Cents | null;
}

/** One payment on the schedule, and whether its money has arrived. */
export interface BalanceLine {
  slot: PaymentSlot;
  label: string;
  amount: Cents;
  amountFormatted: string;
  paid: boolean;
}

export interface Balance {
  /** What the customer agreed to pay. */
  contract: Cents;
  contractFormatted: string;
  /**
   * Of `collected`, the confirmed credits that settled no scheduled payment: a
   * bank transfer, Zelle or card payment that is none of the scheduled
   * amounts. On a payment plan that is money toward the plan (ADR 0286).
   */
  offSchedule?: Cents;
  /** Still owed, in cents. Never negative. */
  outstanding: Cents;
  outstandingFormatted: string;
  /** Whether there is anything to chase. */
  owing: boolean;
  /**
   * Of that, money already sent that the bank has not seen. Usually zero.
   *
   * Kept apart from `outstanding` rather than taken off it: the debt is the
   * debt until the money is in the account (ADR 0139). This is what lets a
   * screen say "$6,630 to collect, $6,630 on its way" instead of picking one
   * of the two and being wrong for somebody.
   */
  onItsWay: Cents;
  onItsWayFormatted: string;
  /**
   * What there is left to actually ask for: `outstanding` less `onItsWay`.
   *
   * The figure the balance letter bills, and from 2026-09-14 the figure the
   * balance card and the production board lead with, so a rep never chases a
   * customer who paid on Friday for money that is still at Stripe.
   */
  toCollect: Cents;
  toCollectFormatted: string;
  /** What has reached the account, at what those slots were owed. */
  collected: Cents;
  collectedFormatted: string;
  /**
   * The schedule, payment by payment, with what has arrived.
   *
   * Exported because a screen that says "$9,000 left" and nothing else invites
   * the next question — left of what, and what came in already. Two on a job
   * sold in halves, four on one sold in draws.
   */
  lines: BalanceLine[];
}

const nothing = (
  contract: Cents = 0 as Cents,
  collected: Cents = 0 as Cents,
  lines: BalanceLine[] = [],
): Balance => ({
  contract,
  contractFormatted: formatMoney(contract),
  offSchedule: 0 as Cents,
  outstanding: 0 as Cents,
  outstandingFormatted: formatMoney(0 as Cents),
  owing: false,
  onItsWay: 0 as Cents,
  onItsWayFormatted: formatMoney(0 as Cents),
  toCollect: 0 as Cents,
  toCollectFormatted: formatMoney(0 as Cents),
  collected,
  collectedFormatted: formatMoney(collected),
  lines,
});

/**
 * The balance on one job.
 *
 * Slot 4 settles the whole thing whatever else has arrived — it is the balance
 * by definition, and a job can be paid in full in one go without a deposit ever
 * being asked for.
 */
export function jobBalance(input: BalanceInput): Balance {
  const contract = cents(Math.max(0, input.contract));
  if (contract <= 0) return nothing();

  const paid = new Set(input.paidSlots.map(Number));

  // The same schedule the estimate builder, the proposal and the payment page
  // use, so no two screens can disagree about what half of a roof is.
  const { first, second, third, final } = expectedPayments({
    contractCents: contract,
    firstCents: input.firstCents ?? null,
    middleCents: input.middleCents ?? null,
    signedContractCents: input.signedContractCents ?? null,
    firstCollected: paid.has(1),
  });

  const lines: BalanceLine[] = (
    [
      [1, first],
      [2, second],
      [3, third],
      [4, final],
    ] as [PaymentSlot, Cents][]
  )
    .filter(([, amount]) => amount > 0)
    .map(([slot, amount]) => ({
      slot,
      label: PAYMENT_SLOT_LABEL[slot] ?? `Payment ${slot}`,
      amount,
      amountFormatted: formatMoney(amount),
      paid: paid.has(slot),
    }));

  /*
   * Every payment whose money is in the account, at what it actually billed.
   *
   * At what it billed, not what reached the bank: a card payment lands short by
   * the processor's cut, and that shortfall is a cost of the job rather than a
   * debt of the customer's (ADR 0095).
   *
   * `billedBySlot` before the schedule row, because on slot 4 they are not the
   * same number. A balance request bills what is genuinely outstanding less
   * anything already on a live link, so it can be larger than the Final row —
   * a job paid off in one go that was never asked for a deposit — or exactly
   * the Final row while an unpaid deposit link is still out there. Reading the
   * schedule alone gets the first case wrong; treating slot 4 as settling
   * everything, which this did until 2026-09-08, gets the second one wrong, and
   * tells a customer who owes half a roof that they owe nothing. Jonathon,
   * 2026-09-08: "we don't send an email to a customer that says they only x
   * when they actually owe y" — which is the same fault read the other way
   * round.
   */
  const billedFor = (line: BalanceLine): Cents => {
    // Non-positive counts as absent, not as nothing collected: a request row
    // with no amount on it says nothing about what the slot was worth.
    const billed = input.billedBySlot?.[line.slot];
    return billed != null && billed > 0 ? cents(billed) : line.amount;
  };
  // Paid links that belong to no slot. Never negative, and capped at the
  // contract by `outstanding` below: a customer who overpays does not start
  // being owed money by the schedule.
  const offSchedule = cents(
    Math.max(0, Math.round(Number(input.offScheduleCents ?? 0))),
  );
  const collected = cents(
    lines.reduce((sum, line) => sum + (line.paid ? billedFor(line) : 0), 0) +
      offSchedule +
      // Monthly checks toward a payment plan, at what each one was for.
      Math.max(0, Math.round(Number(input.planPaidCents ?? 0))),
  );
  const outstanding = cents(Math.max(0, contract - collected));
  // Never more than the debt: a request billed before an earlier payment
  // landed can be larger than what is left, and "-$400 to collect" is not a
  // sentence anybody should read on a job screen.
  const onItsWay = cents(
    Math.min(outstanding, Math.max(0, Math.round(Number(input.onItsWay ?? 0)))),
  );
  const toCollect = cents(Math.max(0, outstanding - onItsWay));

  return {
    contract,
    contractFormatted: formatMoney(contract),
    offSchedule,
    outstanding,
    outstandingFormatted: formatMoney(outstanding),
    owing: outstanding > 0,
    onItsWay,
    onItsWayFormatted: formatMoney(onItsWay),
    toCollect,
    toCollectFormatted: formatMoney(toCollect),
    collected,
    collectedFormatted: formatMoney(collected),
    // Once nothing is outstanding the whole schedule is behind the customer,
    // whichever payment happened to clear it — a job settled by one balance
    // payment should not go on showing "Deposit: not yet" for ever.
    lines:
      outstanding === 0
        ? lines.map((line) => ({ ...line, paid: true }))
        : lines,
  };
}

/**
 * What a list of jobs adds up to, still owed.
 *
 * Written here rather than in a component so the figure on the board and the
 * figure on a card come out of the same arithmetic (CLAUDE.md rule 5).
 */
export function totalOutstanding(balances: readonly Balance[]): {
  outstanding: Cents;
  outstandingFormatted: string;
  /** Of that, money already sent that the bank has not seen. Usually zero. */
  onItsWay: Cents;
  onItsWayFormatted: string;
  /** What there is left to chase: the debt less what is on its way. */
  toCollect: Cents;
  toCollectFormatted: string;
  jobs: number;
} {
  const outstanding = cents(
    balances.reduce((sum, balance) => sum + balance.outstanding, 0),
  );
  const onItsWay = cents(
    balances.reduce((sum, balance) => sum + balance.onItsWay, 0),
  );
  const toCollect = cents(Math.max(0, outstanding - onItsWay));
  return {
    outstanding,
    outstandingFormatted: formatMoney(outstanding),
    onItsWay,
    onItsWayFormatted: formatMoney(onItsWay),
    toCollect,
    toCollectFormatted: formatMoney(toCollect),
    jobs: balances.filter((balance) => balance.owing).length,
  };
}
